class StickyAddToCart extends HTMLElement {
	constructor() {
		super();
		
		this.product = JSON.parse( this.querySelector('[data-product-json]').textContent );
		this.button = this.querySelector('[data-sticky-submit]');
		this.enquire_button = this.querySelector('[data-sticky-enquire]');
		this.variantTitle = this.querySelector('[data-sticky-variant-title]');
		this.price = this.querySelector('[data-sticky-price]');
		this.currentVariant = this.product.variants.find(variant => variant.available) || this.product.variants[0];
		this.abortController = new AbortController();
	}

	connectedCallback() {
		this.button?.addEventListener('click', () => this.addToCart());
		this.enquire_button?.addEventListener('click', () => this.handleEnquiry());
		
		const { signal } = this.abortController;
		// Horizon 4.x replaced variant:selected / variant:update with a single
		// promise-based ProductSelectEvent ('shopify:product:select').
		document.addEventListener('shopify:product:select', this.handleProductSelect, { signal });
		
		this.initRecommendationsObserver();
	}
	
	disconnectedCallback() {
		this.abortController?.abort();
		this.recommendationsObserver?.disconnect();
	}
	
  handleProductSelect = (event) => {
		// Ignore selections coming from product cards / quick-add elsewhere on the page.
		if (event.target instanceof Element && event.target.closest('product-card')) return;

		const promise = event.promise;
		if (!promise) return;

		promise
			.then(({ detail }) => {
				// Combined-listing / multi-form guard.
				if (detail?.productId && String(detail.productId) !== String(this.dataset.productId)) return;

				const variant = detail?.resource;
				if (!variant) {
					this.handleUnavailableVariant();
					return;
				}
				this.updateVariant(variant);
			})
			.catch((error) => {
				if (error?.name !== 'AbortError') console.warn('[aqua-sticky-add-to-cart]', error);
			});
	};

	updateVariant(variant) {
		this.currentVariant = variant;
		
		if (this.variantTitle) {
	    	this.variantTitle.textContent = variant.title;
		}
		
		if (this.price) {
			this.price.textContent = this.formatMoney(variant.price);
		}
		
		if (this.button) {
			this.button.disabled = !variant.available;
	  	this.button.textContent = variant.available ? 'Add to cart' : 'Unavailable';
		}
		
		this.updateSelectedOptions(variant);
	}

  updateSelectedOptions(variant) {
		const optionRows = this.querySelectorAll('[data-selected-option]');
		
		optionRows.forEach(row => {
			const optionIndex = Number(row.dataset.optionIndex);
			const selectedValue = variant.options?.[optionIndex];
			
	  	  	if (!selectedValue) return;
			
	  	  	const valueEl = row.querySelector('[data-selected-option-value]');
			const swatchEl = row.querySelector('[data-selected-option-swatch]');
			
			if (valueEl) {
				valueEl.textContent = selectedValue;
			}
			
			if (swatchEl) {
				this.updateSwatch(row, swatchEl, selectedValue);
			}
		});
	}

  updateSwatch(row, swatchEl, selectedValue) {
		const template = row.querySelector('template[data-swatch-templates]');
		
		if (!template) return;
		
		const match = template.content.querySelector(
			`[data-swatch-template][data-value="${CSS.escape(selectedValue)}"]`
		);
		
		swatchEl.innerHTML = match ? match.innerHTML : '';
	}

  readSelectedOptionsFromPicker() {
		const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`)
			|| document.querySelector('variant-picker');
		if (!picker) return [];
		const items = [];
		picker.querySelectorAll('input[type="radio"]:checked').forEach((inp) => {
			const pos = parseInt((inp.dataset.inputId || '').split('-')[0], 10);
			items.push({ pos: isNaN(pos) ? items.length : pos, value: inp.value });
		});
		picker.querySelectorAll('select').forEach((sel) => {
			const opt = sel.options[sel.selectedIndex];
			const pos = parseInt(((opt && opt.dataset.inputId) || '').split('-')[0], 10);
			items.push({ pos: isNaN(pos) ? items.length : pos, value: sel.value });
		});
		return items.sort((a, b) => a.pos - b.pos).map((i) => i.value);
	}

  handleUnavailableVariant() {
		const selectedOptions = this.readSelectedOptionsFromPicker();

		if (!selectedOptions.length) return;
		
		const optionRows = this.querySelectorAll('[data-selected-option]');
		
		optionRows.forEach(row => {
	  	const optionIndex = Number(row.dataset.optionIndex);
	  	const selectedValue = selectedOptions[optionIndex];
			
	  	if (!selectedValue) return;
			
	  	const valueEl = row.querySelector('[data-selected-option-value]');
	  	const countEl = row.querySelector('[data-selected-option-count]');
	  	const swatchEl = row.querySelector('[data-selected-option-swatch]');
			
	  	if (valueEl) valueEl.textContent = selectedValue;
	  	if (countEl) countEl.textContent = '0 available';
	  	if (swatchEl) this.updateSwatch(row, swatchEl, selectedValue);
		});
		
		if (this.button) {
	  	this.button.disabled = true;
	  	this.button.textContent = 'Unavailable';
		}
  }
  
  findTargetAddToCartForm() {
		const productId = this.dataset.productId;
	  
		return document.querySelector(
			`product-form-component[data-product-id="${productId}"] [action*="/cart/add"]`
		);
  }
  
  async addToCart() {
		const targetForm = this.findTargetAddToCartForm();
  	
		if (!targetForm) return;
  	
		targetForm.requestSubmit();
  	
		this.button.dataset.added = 'true';
		this.button.textContent = 'Added';
  	
		setTimeout(() => {
		  this.button.removeAttribute('data-added');
		  this.updateVariant(this.currentVariant);
		}, 1200);
  }

  formatMoney(cents) {
		const currency = window.Shopify?.currency?.active || 'USD';
		const locale = window.Shopify?.locale || 'en-US';
		// Resolve the currency's native decimal precision (0 for JPY, 2 for GBP/USD, 3 for KWD…)
		const { minimumFractionDigits: nativeDecimals } = new Intl.NumberFormat(locale, {
			style: 'currency', currency,
		}).resolvedOptions();
		const divisor = Math.pow(10, nativeDecimals);
		// Mirror money_without_trailing_zeros: omit decimals only when amount is a whole unit
		const isWholeUnit = cents % divisor === 0;
		// narrowSymbol yields "$" (not "A$"); append the ISO code to mirror the Liquid
		// money_with_currency format, e.g. "$1,868 AUD".
		const amount = new Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
			currencyDisplay: 'narrowSymbol',
			minimumFractionDigits: isWholeUnit ? 0 : nativeDecimals,
			maximumFractionDigits: nativeDecimals,
		}).format(cents / divisor);
		return `${amount} ${currency}`;
  }
	
	handleEnquiry() {
		// .custom-price-atc is the main product-page "Request Quote" / add-to-cart
		// button, pasted into the theme customiser from "custom code/Add to cart
		// button.html". Falls back to the contact page if it's ever missing.
		const main_enquiry_button = document.querySelector('.custom-price-atc');
		main_enquiry_button ? main_enquiry_button.click() : window.location.href = '/pages/contact';
	}
	
	initRecommendationsObserver() {
		const recommendations = document.querySelector('.shopify-section[id*="recommendations"]');
		if (!recommendations) return;
		
		this.recommendationsObserver = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				this.classList.toggle('is-hidden', entry.isIntersecting);
			});
		}, { threshold: 0 });
		this.recommendationsObserver.observe(recommendations);
	}
}

if (!customElements.get('aqua-sticky-add-to-cart')) {
  customElements.define('aqua-sticky-add-to-cart', StickyAddToCart);
}
